import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateDeviceMutation } from '../state/api';
import styles from './CreateDevice.module.css';
import { FaPlus, FaSave, FaTimes } from 'react-icons/fa';

export default function CreateDevice() {
  const navigate = useNavigate();
  const [createDevice, { isLoading }] = useCreateDeviceMutation();

  // Form state
  const [location, setLocation] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [defaultHumidity, setDefaultHumidity] = useState<number>(95);
  const [defaultDivergence, setDefaultDivergence] = useState<number>(1);
  const [ipAddress, setIpAddress] = useState<string>('');
  const [status, setStatus] = useState<string>('active');
  const [samplingInterval, setSamplingInterval] = useState<number>(30000);

  // Pins
  const [relay1, setRelay1] = useState<number>(2);
  const [relay2, setRelay2] = useState<number>(4);
  const [relay3, setRelay3] = useState<number>(5);
  const [ventil, setVentil] = useState<number>(6);
  const [humTempSensor, setHumTempSensor] = useState<number>(33);

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const result = await createDevice({
        location: location.trim() || undefined,
        name: name.trim() || undefined,
        defaultHumidity,
        defaultDivergence,
        ipAddress: ipAddress.trim() || undefined,
        status: status || 'active',
        samplingInterval,
        pins: {
          relay1,
          relay2,
          relay3,
          ventil,
          humTempSensor,
        },
      }).unwrap();

      setSuccess(`Zařízení "${result.deviceId || result._id}" bylo úspěšně vytvořeno!`);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.message || 'Chyba při vytváření zařízení');
      console.error('Error creating device:', err);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className={styles.createDevicePage}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>
            <FaPlus /> Vytvořit nové zařízení
          </h1>
          <p className={styles.subtitle}>Vyplňte údaje pro nové zařízení v databázi</p>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        {success && <div className={styles.successMessage}>{success}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Basic Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Základní informace</h2>
            <div className={styles.infoBox}>
              <p>
                Device ID bude automaticky vygenerován jako MongoDB ObjectId při vytvoření zařízení.
              </p>
            </div>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="name">Název zařízení</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="např. TIS #1"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="location">Lokace</label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="např. Greenhouse #1"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="ipAddress">IP Adresa</label>
                <input
                  id="ipAddress"
                  type="text"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  placeholder="např. 192.168.0.69:4000"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="status">Status</label>
                <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">Aktivní</option>
                  <option value="inactive">Neaktivní</option>
                  <option value="maintenance">Údržba</option>
                </select>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="samplingInterval">Sampling Interval (ms)</label>
                <input
                  id="samplingInterval"
                  type="number"
                  value={samplingInterval}
                  onChange={(e) => setSamplingInterval(Number(e.target.value))}
                  min="1000"
                  step="1000"
                />
              </div>
            </div>
          </div>

          {/* Default Values */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Výchozí hodnoty</h2>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="defaultHumidity">Výchozí vlhkost (%)</label>
                <input
                  id="defaultHumidity"
                  type="number"
                  value={defaultHumidity}
                  onChange={(e) => setDefaultHumidity(Number(e.target.value))}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="defaultDivergence">Divergence (%)</label>
                <input
                  id="defaultDivergence"
                  type="number"
                  value={defaultDivergence}
                  onChange={(e) => setDefaultDivergence(Number(e.target.value))}
                  min="0"
                  max="10"
                  step="0.1"
                />
              </div>
            </div>
          </div>

          {/* Pins Configuration */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Konfigurace PINů</h2>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="relay1">Relay 1</label>
                <input
                  id="relay1"
                  type="number"
                  value={relay1}
                  onChange={(e) => setRelay1(Number(e.target.value))}
                  min="0"
                  max="40"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="relay2">Relay 2</label>
                <input
                  id="relay2"
                  type="number"
                  value={relay2}
                  onChange={(e) => setRelay2(Number(e.target.value))}
                  min="0"
                  max="40"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="relay3">Relay 3</label>
                <input
                  id="relay3"
                  type="number"
                  value={relay3}
                  onChange={(e) => setRelay3(Number(e.target.value))}
                  min="0"
                  max="40"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="ventil">Ventil</label>
                <input
                  id="ventil"
                  type="number"
                  value={ventil}
                  onChange={(e) => setVentil(Number(e.target.value))}
                  min="0"
                  max="40"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="humTempSensor">Humidity/Temp Sensor</label>
                <input
                  id="humTempSensor"
                  type="number"
                  value={humTempSensor}
                  onChange={(e) => setHumTempSensor(Number(e.target.value))}
                  min="0"
                  max="40"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button type="button" onClick={handleCancel} className={styles.cancelButton}>
              <FaTimes /> Zrušit
            </button>
            <button type="submit" disabled={isLoading} className={styles.submitButton}>
              <FaSave /> {isLoading ? 'Vytváření...' : 'Vytvořit zařízení'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
